import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import ConfirmModal from '../../components/ConfirmModal';
import API, { resolvePhoto } from '../../utils/api';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import { id } from 'date-fns/locale';
import StatusBadge from '../../components/StatusBadge';
import { ArrowLeft } from 'lucide-react';
import './ProducerArticleReview.css';

export default function ProducerArticleReview() {
  const { id: articleId } = useParams();
  const navigate = useNavigate();

  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('preview');
  const [decision, setDecision] = useState('');
  const [revisionNotes, setRevisionNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [confirmPublish, setConfirmPublish] = useState(false);

  useEffect(() => { fetchArticle(); }, [articleId]);

  const fetchArticle = async () => {
    try {
      setLoading(true);
      const res = await API.get('/articles/producer/queue');
      const all = res.data.articles || [];
      const found = all.find(a => a._id === articleId);
      if (found) {
        setArticle(found);
      } else {
        toast.error('Artikel tidak ditemukan');
        navigate('/producer');
      }
    } catch {
      toast.error('Gagal memuat artikel');
      navigate('/producer');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!decision) { toast.error('Pilih keputusan terlebih dahulu'); return; }
    if (decision !== 'approve' && !revisionNotes.trim()) {
      toast.error('Catatan revisi wajib diisi'); return;
    }
    try {
      setSubmitting(true);
      if (decision === 'approve') {
        await API.patch(`/articles/producer/${articleId}/approve`);
        toast.success('Artikel disetujui dan dikirim ke Reader');
      } else if (decision === 'revise') {
        await API.patch(`/articles/producer/${articleId}/revise`, { notes: revisionNotes });
        toast.success('Catatan revisi dikirim ke Editor');
      }
      navigate('/producer');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gagal mengirim keputusan');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <DashboardLayout>
      <div className="producer-loading">
        <div className="spinner" />
      </div>
    </DashboardLayout>
  );

  if (!article) return null;

  const canDecide = article.status === 'review_producer';
  const canPublish = article.status === 'approved';
  const isPublished = article.status === 'published';
  const liveEdit = article.liveEdit || {};

  const handlePublish = async () => {
    setConfirmPublish(false);
    try {
      setPublishing(true);
      await API.patch(`/articles/producer/${articleId}/publish`);
      toast.success('Artikel berhasil dipublikasikan!');
      navigate('/producer');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gagal mempublikasikan');
    } finally {
      setPublishing(false);
    }
  };

  const handleRequestLiveEdit = async () => {
    const note = window.prompt('Catatan perubahan untuk editor (opsional):', '');
    if (note === null) return; // batal
    try {
      await API.patch(`/articles/producer/${articleId}/request-live-edit`, { note });
      toast.success('Permintaan edit dikirim ke editor. Versi lama tetap tayang.');
      fetchArticle();
    } catch (err) { toast.error(err.response?.data?.message || 'Gagal mengirim permintaan'); }
  };

  const handleVerifyLiveEdit = async (approve) => {
    try {
      await API.patch(`/articles/producer/${articleId}/verify-live-edit`, { approve });
      toast.success(approve ? 'Perubahan diterapkan & artikel diperbarui.' : 'Dikembalikan ke editor.');
      fetchArticle();
    } catch (err) { toast.error(err.response?.data?.message || 'Gagal memproses'); }
  };

  const handleDeletePublished = async () => {
    if (!window.confirm('Hapus artikel terpublikasi ini secara permanen? Tindakan tidak dapat dibatalkan.')) return;
    try {
      await API.delete(`/articles/producer/${articleId}`);
      toast.success('Artikel terpublikasi dihapus.');
      navigate('/producer');
    } catch (err) { toast.error(err.response?.data?.message || 'Gagal menghapus'); }
  };

  return (
    <DashboardLayout>
      <div className="producer-review-page">

        {/* Header */}
        <div className="review-header">
          <div className="review-header-left">
            <button className="back-btn" onClick={() => navigate('/producer')}>
              <ArrowLeft size={14} style={{ marginRight: 4 }} />
              Kembali
            </button>
            <span className="article-category">{article.category?.name}</span>
            <StatusBadge status={article.status} />
          </div>
          {canDecide && (
            <div className="review-header-note">
              Artikel menunggu keputusan Anda
            </div>
          )}
        </div>

        <div className="review-layout">

          {/* Content area */}
          <div className="review-content-area">
            {/* Tabs */}
            <div className="review-tabs">
              <button className={`rtab ${activeTab === 'preview' ? 'active' : ''}`} onClick={() => setActiveTab('preview')}>
                Preview Artikel
              </button>
              <button className={`rtab ${activeTab === 'brief' ? 'active' : ''}`} onClick={() => setActiveTab('brief')}>
                Brief dari Reader
              </button>
              {article.photos?.length > 0 && (
                <button className={`rtab ${activeTab === 'photos' ? 'active' : ''}`} onClick={() => setActiveTab('photos')}>
                  Foto ({article.photos.length})
                </button>
              )}
            </div>

            <div className="review-tab-body">

              {/* Preview Tab */}
              {activeTab === 'preview' && (
                <div className="article-preview">
                  <div className="preview-meta-row">
                    {article.assignedEditor?.name && (
                      <span>Editor: <strong>{article.assignedEditor.name}</strong></span>
                    )}
                    <span>
                      Diperbarui {formatDistanceToNow(new Date(article.updatedAt), { addSuffix: true, locale: id })}
                    </span>
                  </div>
                  {article.photos?.[article.featuredPhoto || 0]?.url && (
                    <img
                      className="preview-cover"
                      src={resolvePhoto(article.photos[article.featuredPhoto || 0].url)}
                      alt={article.title}
                    />
                  )}
                  <h1 className="preview-title">{article.title || 'Judul belum diisi'}</h1>
                  {article.excerpt && <p className="preview-excerpt">{article.excerpt}</p>}
                  <div className="article-body" dangerouslySetInnerHTML={{ __html: article.content || '<p style="color:var(--ki-text-faint);font-style:italic">Konten belum ditulis.</p>' }} />
                </div>
              )}

              {/* Brief Tab */}
              {activeTab === 'brief' && (
                <div className="article-brief">
                  <div className="brief-grid">
                    {[
                      { label: 'Apa (What)', value: article.what },
                      { label: 'Siapa (Who)', value: article.who },
                      { label: 'Kapan (When)', value: article.when },
                      { label: 'Di mana (Where)', value: article.where },
                      { label: 'Mengapa (Why)', value: article.why },
                      { label: 'Bagaimana (How)', value: article.how },
                    ].map(({ label, value }) => (
                      <div key={label} className="brief-item">
                        <label>{label}</label>
                        <p>{value || <em style={{ opacity: 0.5 }}>Tidak diisi</em>}</p>
                      </div>
                    ))}
                  </div>
                  {article.additionalContext && (
                    <div className="brief-item" style={{ gridColumn: 'span 2', marginTop: 12 }}>
                      <label>Konteks Tambahan</label>
                      <p>{article.additionalContext}</p>
                    </div>
                  )}
                  {article.revisions?.length > 0 && (
                    <div style={{ marginTop: 24 }}>
                      <div className="section-title">Riwayat Revisi</div>
                      {article.revisions.map((rev, i) => (
                        <div key={i} style={{ padding: '12px 16px', background: 'rgba(255,255,255,0.03)', border: '1px solid var(--ki-border)', borderRadius: 8, marginBottom: 10 }}>
                          <div style={{ fontSize: 12, color: 'var(--ki-text-faint)', marginBottom: 4 }}>
                            Dari: <strong style={{ textTransform: 'capitalize', color: 'var(--ki-text-muted)' }}>{rev.from}</strong>
                            {' · '}{formatDistanceToNow(new Date(rev.createdAt), { addSuffix: true, locale: id })}
                          </div>
                          <div style={{ fontSize: 14 }}>{rev.notes}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Photos Tab */}
              {activeTab === 'photos' && (
                <div className="review-photos">
                  <div className="photos-grid-review">
                    {article.photos?.map((ph, i) => (
                      <div key={i}>
                        <img className="photo-thumb" src={resolvePhoto(ph.url)} alt={ph.caption || `Foto ${i + 1}`} />
                        {ph.caption && <p className="photo-caption">{ph.caption}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>
          </div>

          {/* Decision sidebar */}
          <div className="review-sidebar">
            <div className="review-decision-box">
              <div className="decision-title">
                {canDecide ? 'Keputusan Review' : 'Status Artikel'}
              </div>

              {canPublish ? (
                <div>
                  <StatusBadge status={article.status} />
                  <p style={{ fontSize: 13, color: 'var(--ki-text-faint)', marginTop: 12, marginBottom: 16 }}>
                    Pengaju telah menyetujui artikel ini. Siap untuk dipublikasikan.
                  </p>
                  <button
                    className="btn-submit-review approve"
                    onClick={() => setConfirmPublish(true)}
                    disabled={publishing}
                    style={{ width: '100%' }}
                  >
                    {publishing ? 'Mempublikasikan...' : 'Publikasikan Artikel'}
                  </button>
                </div>
              ) : isPublished ? (
                <div>
                  <StatusBadge status={article.status} />
                  {liveEdit.inProgress ? (
                    <div style={{ marginTop: 14 }}>
                      <div style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#92400E', marginBottom: 14, lineHeight: 1.5 }}>
                        {liveEdit.stage === 'review_producer'
                          ? 'Editor telah menyelesaikan perubahan. Versi lama masih tayang — verifikasi untuk menerapkan.'
                          : 'Editor sedang mengerjakan perubahan. Versi lama tetap tayang di portal.'}
                        {liveEdit.note ? <div style={{ marginTop: 6, fontStyle: 'italic' }}>Catatan: {liveEdit.note}</div> : null}
                      </div>
                      {liveEdit.stage === 'review_producer' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <button className="btn btn-primary" onClick={() => handleVerifyLiveEdit(true)}>Terapkan & Perbarui Artikel</button>
                          <button className="btn btn-ghost" onClick={() => handleVerifyLiveEdit(false)}>Kembalikan ke Editor</button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 14 }}>
                      <p style={{ fontSize: 13, color: 'var(--ki-text-faint)', marginBottom: 4 }}>Artikel ini sudah tayang di portal.</p>
                      <button className="btn btn-secondary" onClick={handleRequestLiveEdit}
                        style={{ width: '100%', whiteSpace: 'normal', height: 'auto', lineHeight: 1.4, textAlign: 'center', padding: '10px 14px' }}>
                        Minta Editor Perbarui
                        <span style={{ display: 'block', fontSize: 11, fontWeight: 400, opacity: 0.7, marginTop: 2 }}>tanpa mencabut publikasi</span>
                      </button>
                      <button className="btn" onClick={handleDeletePublished}
                        style={{ width: '100%', background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.3)', color: '#DC2626', whiteSpace: 'normal', height: 'auto', lineHeight: 1.4, padding: '10px 14px' }}>
                        Hapus Artikel
                      </button>
                    </div>
                  )}
                </div>
              ) : !canDecide ? (
                <div>
                  <StatusBadge status={article.status} />
                  <p style={{ fontSize: 13, color: 'var(--ki-text-faint)', marginTop: 12 }}>
                    Artikel ini sudah melewati tahap review producer.
                  </p>
                </div>
              ) : (
                <>
                  <div className="decision-options">
                    <button
                      className={`decision-btn approve ${decision === 'approve' ? 'selected' : ''}`}
                      onClick={() => setDecision('approve')}
                    >
                      Setujui: Kirim ke Reader
                    </button>
                    <button
                      className={`decision-btn revise ${decision === 'revise' ? 'selected' : ''}`}
                      onClick={() => setDecision('revise')}
                    >
                      Minta Revisi ke Editor
                    </button>
                  </div>

                  {decision && decision !== 'approve' && (
                    <textarea
                      className="revision-textarea"
                      rows={5}
                      placeholder="Tuliskan catatan revisi atau alasan penolakan secara jelas..."
                      value={revisionNotes}
                      onChange={e => setRevisionNotes(e.target.value)}
                    />
                  )}

                  {decision && (
                    <button
                      className={`btn-submit-review ${decision}`}
                      onClick={handleSubmit}
                      disabled={submitting || (decision !== 'approve' && !revisionNotes.trim())}
                    >
                      {submitting ? 'Memproses...'
                        : decision === 'approve' ? 'Setujui Artikel'
                        : decision === 'revise' ? 'Kirim Revisi'
                        : 'Tolak Artikel'}
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Article info — ProducerArticleReview sidebar */}
            <div className="ki-card" style={{ padding: 16, marginTop: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--ki-text-faint)', marginBottom: 12 }}>Info Artikel</div>
              {[
                { label: 'Pengaju', value: article.requestedBy?.name },
                { label: 'Editor', value: article.assignedEditor?.name || '-' },
                { label: 'Kategori', value: article.category?.name || '-' },
                { label: 'Foto', value: `${article.photos?.length || 0} foto` },
              ].map(({ label, value }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 8 }}>
                  <span style={{ color: 'var(--ki-text-faint)' }}>{label}</span>
                  <span style={{ color: 'var(--ki-text-muted)', fontWeight: 600 }}>{value}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
      <ConfirmModal
        open={confirmPublish}
        title="Publikasikan Artikel"
        message="Artikel ini akan dipublikasikan dan dapat dibaca oleh publik. Tindakan ini tidak dapat dibatalkan."
        confirmLabel="Publikasikan"
        onConfirm={handlePublish}
        onCancel={() => setConfirmPublish(false)}
        loading={publishing}
      />
    </DashboardLayout>
  );
}
